import type { LucideIcon } from "lucide-react";
import { Home, KeyRound, Sparkles } from "lucide-react";

export type ServiceId = "regular" | "deep" | "tenancy";

export type PricePackage = {
  id: string;
  property: string;
  duration: string;
  price: number;
  note?: string;
};

export type ServicePriceList = {
  title: string;
  description: string;
  rates: PricePackage[];
  note?: string;
};

export type Service = {
  id: ServiceId;
  name: string;
  shortName: string;
  description: string;
  startingAt: number;
  duration: string;
  icon: LucideIcon;
  bullets: string[];
  tone: "moss" | "sun" | "clay" | "sky" | "ink";
};

export const services: Service[] = [
  {
    id: "regular",
    name: "Standard clean",
    shortName: "Standard clean",
    description:
      "A reliable clean for homes that are regularly maintained. Arrange a weekly, fortnightly or monthly visit.",
    startingAt: 27,
    duration: "from 1.5 hours",
    icon: Home,
    bullets: ["Bathrooms and living areas", "Vacuuming and mopping", "Dusting and kitchen surfaces"],
    tone: "moss",
  },
  {
    id: "deep",
    name: "Deep clean",
    shortName: "Deep clean",
    description:
      "A thorough reset of your space, including appliances, inside cupboards, interior windows and the details that need extra attention.",
    startingAt: 150,
    duration: "from 2.5 hours",
    icon: Sparkles,
    bullets: ["Kitchen appliances and cupboards", "Bathrooms, touchpoints and doors", "Skirtings, windows and floors"],
    tone: "sun",
  },
  {
    id: "tenancy",
    name: "End-of-tenancy clean",
    shortName: "End of tenancy",
    description:
      "A room-by-room handover clean covering bathrooms, kitchen, appliances, cupboards, windows and living spaces.",
    startingAt: 150,
    duration: "priced by property size",
    icon: KeyRound,
    bullets: ["Inside and outside appliances", "Inside and outside cupboards", "Move-out checklist throughout"],
    tone: "clay",
  },
];

export const servicePriceLists: Record<ServiceId, ServicePriceList> = {
  regular: {
    title: "Standard clean",
    description: "Regular home cleaning is £18 per hour and can be booked weekly, fortnightly or monthly.",
    note: "Monthly cleans are also listed at £175. Please confirm the home size and scope before booking.",
    rates: [
      { id: "regular-one-one", property: "1 bed, 1 bath", duration: "1.5 hours", price: 27 },
      { id: "regular-one-extra-toilet", property: "1 bed, 1 bath + extra toilet", duration: "2 hours", price: 36 },
      { id: "regular-two-one", property: "2 bed, 1 bath", duration: "2 hours", price: 36 },
      { id: "regular-two-one-extra-toilet", property: "2 bed, 1 bath + extra toilet", duration: "2.5 hours", price: 45 },
      { id: "regular-two-two", property: "2 bed, 2 bath", duration: "3 hours", price: 54 },
      { id: "regular-three-one", property: "3 bed, 1 bath", duration: "2.5 hours", price: 45 },
      { id: "regular-three-two", property: "3 bed, 2 bath", duration: "2.5 hours", price: 45 },
      { id: "regular-four-one", property: "4 bed, 1 bath", duration: "3 hours", price: 54 },
      { id: "regular-four-one-extra-toilet", property: "4 bed, 1 bath + extra toilet", duration: "3 hours", price: 54 },
      { id: "regular-four-two", property: "4 bed, 2 bath", duration: "3.5 hours", price: 63 },
      { id: "regular-five-one", property: "5 bed, 1 bath", duration: "4 hours", price: 72 },
      { id: "regular-five-two", property: "5 bed, 2 bath", duration: "4 hours", price: 72 },
      { id: "regular-five-four", property: "5 bed, 4 bathrooms", duration: "5 hours", price: 90 },
    ],
  },
  deep: {
    title: "Deep clean",
    description: "A detailed, top-to-bottom clean. Time and price are based on the property size and bathroom count.",
    rates: [
      { id: "deep-one-one", property: "1 bed, 1 bath", duration: "2.5 hours", price: 150 },
      { id: "deep-two-one", property: "2 bed, 1 bath", duration: "4.5 hours", price: 220, note: "Flexible quote: £190–£200 for some homes." },
      { id: "deep-two-one-extra-toilet", property: "2 bed, 1 bath + extra toilet", duration: "5 hours", price: 200 },
      { id: "deep-two-two", property: "2 bed, 2 bath", duration: "3.5 hours", price: 180 },
      { id: "deep-three-one", property: "3 bed, 1 bath", duration: "5 hours", price: 225 },
      { id: "deep-three-two", property: "3 bed, 2 bath", duration: "6.5 hours", price: 230 },
      { id: "deep-four-one", property: "4 bed, 1 bath", duration: "6.5 hours", price: 240 },
      { id: "deep-four-one-extra-toilet", property: "4 bed, 1 bath + extra toilet", duration: "6.5 hours", price: 245 },
      { id: "deep-four-two", property: "4 bed, 2 bath", duration: "6.5 hours", price: 250 },
      { id: "deep-five-one", property: "5 bed, 1 bath", duration: "7 hours", price: 250 },
      { id: "deep-five-two", property: "5 bed, 2 bath", duration: "7 hours", price: 260 },
      { id: "deep-five-four", property: "5 bed, 4 bathrooms", duration: "8 hours", price: 300 },
    ],
  },
  tenancy: {
    title: "End-of-tenancy clean",
    description: "A full move-out clean, priced by property size and confirmed before the appointment.",
    rates: [
      { id: "tenancy-studio", property: "Studio", duration: "To be confirmed", price: 150 },
      { id: "tenancy-one-one", property: "1 bed, 1 bath", duration: "To be confirmed", price: 190 },
      { id: "tenancy-two-one", property: "2 bed, 1 bath", duration: "6 hours", price: 230 },
      { id: "tenancy-two-two", property: "2 bed, 2 bath", duration: "5 hours", price: 240 },
      { id: "tenancy-three-one", property: "3 bed, 1 bath", duration: "5–6 hours", price: 250 },
      { id: "tenancy-three-two", property: "3 bed, 2 bath", duration: "5–6 hours", price: 265 },
      { id: "tenancy-four-one", property: "4 bed, 1 bath", duration: "6.5 hours", price: 290 },
      { id: "tenancy-four-one-extra-toilet", property: "4 bed, 1 bath + extra toilet", duration: "6.5 hours", price: 295 },
      { id: "tenancy-four-two", property: "4 bed, 2 bath", duration: "6.5 hours", price: 300 },
      { id: "tenancy-five-one", property: "5 bed, 1 bath", duration: "7 hours", price: 315 },
      { id: "tenancy-five-one-extra-toilet", property: "5 bed, 1 bath + extra toilet", duration: "To be confirmed", price: 325 },
      { id: "tenancy-five-two", property: "5 bed, 2 bath", duration: "7.5 hours", price: 325 },
      { id: "tenancy-five-four", property: "5 bed, 4 bathrooms", duration: "To be confirmed", price: 340 },
      { id: "tenancy-six-two", property: "6 bed, 2 bath", duration: "To be confirmed", price: 400 },
    ],
  },
};

export const extras = [
  { id: "fridge", name: "Inside the fridge", price: 15 },
  { id: "cupboards", name: "Inside cupboards", price: 25 },
  { id: "oven", name: "Single oven clean", price: 50 },
  { id: "double-oven", name: "Double oven clean", price: 65 },
];

export const pricingExtras = [
  {
    title: "Standard clean add-ons",
    items: [
      { name: "Inside fridge cleaning", price: "£15" },
      { name: "Inside cupboard cleaning", price: "£25" },
      { name: "Carpet cleaning", price: "£40 per room", note: "Price depends on room size." },
      { name: "Single oven clean", price: "£50", note: "Add £15 if it is heavily soiled." },
      { name: "Double oven clean", price: "£65" },
      { name: "Bathroom deep clean", price: "Price on request" },
    ],
  },
  {
    title: "Deep clean and end-of-tenancy extras",
    items: [
      { name: "Carpet cleaning", price: "£25 per room", note: "Price depends on room size." },
      { name: "Very heavily soiled oven during a deep clean", price: "+£15" },
      { name: "Mini deep clean: kitchen and bathroom", price: "From £60" },
    ],
  },
];

export const standardCleanIncludes = [
  "Bathroom cleaning: toilet, bath or shower, mirror and sink",
  "Vacuuming and mopping floors",
  "Tidying where needed and dusting",
  "Kitchen surface cleaning and wiping the outside of appliances",
  "Living areas cleaned and vacuumed",
];

export const deepCleanChecklist = [
  {
    title: "General areas",
    items: ["Empty bins", "Vacuum and mop floors", "Dust surfaces and furniture", "Clean skirting boards", "Clean interior windows", "Wipe doors, handles, light switches and touchpoints"],
  },
  {
    title: "Kitchen",
    items: ["Clean microwave and fridge", "Wipe worktops and sink", "Clean hob", "Clean inside cupboards", "Clean oven"],
  },
  {
    title: "Bedrooms",
    items: ["Change sheets if supplied", "Dust and clean skirting boards", "Vacuum and mop floors", "Carpet cleaning available as an extra"],
  },
  {
    title: "Bathroom",
    items: ["Clean bath or shower, toilet and sink", "Buff mirrors", "Remove visible mould where suitable", "Mop floors"],
  },
];

export const tenancyIncludes = [
  "Clean bathrooms and kitchen",
  "Clean skirting boards and interior windows",
  "Clean the inside and outside of appliances, including oven, fridge and washing machine",
  "Clean the inside and outside of cupboards",
  "Vacuum and mop floors",
  "Dust and polish all living spaces",
];

export const deepCleanClientMessage =
  "Our deep clean is a thorough reset of your space. It includes inside cupboards, interior windows, oven, microwave and fridge cleaning, plus doors, handles, touchpoints, light switches and skirting boards, followed by vacuuming and mopping.";

export const pricingNotes = [
  {
    title: "Prices by property size",
    copy: "Choose the closest home size and bathroom count. We confirm the final scope before accepting a booking.",
  },
  {
    title: "Extras are itemised",
    copy: "Carpet and heavily soiled appliance add-ons are listed separately so you can plan ahead.",
  },
  {
    title: "Need help choosing?",
    copy: "Contact us before booking if you are unsure which service or property size fits your home.",
  },
];

export const faqs = [
  {
    question: "What should I do before my cleaner arrives?",
    answer:
      "A little decluttering helps us spend the visit cleaning rather than moving belongings. Otherwise, just leave access details and any priorities in your booking notes.",
  },
  {
    question: "Do I need to be at home?",
    answer:
      "That is entirely up to you. Let us know your preferred access arrangement when you book, and add any instructions that would make the visit easier.",
  },
  {
    question: "Can I request particular products?",
    answer:
      "Yes. You can add product preferences, sensitivities, and areas to avoid in the notes field. We’ll confirm anything that needs a little more planning.",
  },
  {
    question: "How is my estimate calculated?",
    answer:
      "The guide price reflects the service and home size you choose. Extra tasks and property specifics can affect the final estimate, which is always confirmed before the job is accepted.",
  },
  {
    question: "What if I need to move my booking?",
    answer:
      "Please contact the team with your booking reference as soon as you can. We’ll help find the next workable slot.",
  },
];

export type LocalBookingRequest = {
  id: string;
  customer: string;
  email: string;
  service: string;
  date: string;
  time: string;
  total: number;
  status: "New";
  address?: string;
};

const storageKey = "best-one-cleans-booking-requests";

function isLocalBookingRequest(value: unknown): value is LocalBookingRequest {
  if (!value || typeof value !== "object") return false;
  const booking = value as Record<string, unknown>;
  return typeof booking.id === "string"
    && typeof booking.customer === "string"
    && typeof booking.email === "string"
    && typeof booking.service === "string"
    && typeof booking.date === "string"
    && typeof booking.time === "string"
    && typeof booking.total === "number"
    && booking.status === "New";
}

function readLocalBookingRequests(): LocalBookingRequest[] {
  try {
    const value = window.localStorage.getItem(storageKey);
    if (!value) return [];
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(isLocalBookingRequest) : [];
  } catch {
    return [];
  }
}

export function saveLocalBooking(booking: LocalBookingRequest): boolean {
  try {
    const current = readLocalBookingRequests();
    window.localStorage.setItem(storageKey, JSON.stringify([booking, ...current]));
    return true;
  } catch {
    return false;
  }
}

export function formatMoney(amount: number) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatBookingDate(value: string) {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(`${value}T12:00:00`));
}
